import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { sharedMutators } from "../../shared/mutators";
import { namesMatch, normalizeName } from "../../shared/normalizeName";
import { queries } from "../../shared/queries";
import type { GrantRow } from "../../shared/rbac/grantQueries";
import {
  type BuiltinRoleId,
  type GrantEntry,
  grantHasPermission,
  type RoleOption,
  rolesForScope,
  SCOPE_MANAGE_GRANTS_PERMISSION,
} from "../../shared/rbac/roles";
import { defaultRoleForScope } from "../lib/defaultRoleForScope";
import { getWritableScopeIds } from "../lib/getWritableScopeIds";
import { grantEntriesToRows } from "../lib/grantAssembly";
import { deriveScopeGrantDelta } from "../lib/spaceGrantDelta";
import { computeSpaceMemberSupplierGap } from "../lib/spaceMemberSupplierGap";
import {
  grantsWithoutSpacePrincipals,
  removedPrincipalPreviews,
} from "../lib/spaceSupplierAccess";
import { spacePublicHostnameSuffix } from "../../shared/publicUrl";
import { MAX_SLUG_LENGTH } from "../../shared/slug";
import { usableSuppliersForSpace } from "../lib/usableSuppliersForSpace";
import { usedSkillsForSpace } from "../lib/usedSuppliersForSpace";
import { MAX_SPACE_NAME_LENGTH } from "../../shared/spaceName";
import {
  useAffectedChatCount,
  useConnectorDisconnectImpact,
} from "../hooks/useConnectorDisconnectImpact";
import { useCurrentUserGrants } from "../hooks/useCurrentUserGrants";
import { useGrantSpaceAccess } from "../hooks/useGrantSpaceAccess";
import { useGroupMemberships } from "../hooks/useGroupMemberships";
import { useNameField } from "../hooks/useNameField";
import { useQuery } from "../hooks/useQuery";
import { useSlugField } from "../hooks/useSlugField";
import { useTenantCustomDomain } from "../hooks/useTenantCustomDomain";
import { publicDomain } from "../lib/tenantOrigin";
import { useScopeGrants } from "../hooks/useScopeGrants";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { useSpaceUsedSuppliers } from "../hooks/useSpaceUsedSuppliers";
import { useSpaces } from "../hooks/useSpaces";
import { PlaceholderAvatar } from "./Avatar";
import { Box } from "./Box";
import { ConfirmModal } from "./ConfirmModal";
import { ConnectorsList } from "./ConnectorsList";
import { DangerSection } from "./Settings/DangerSection";
import { DeleteSpaceModal } from "./DeleteSpaceModal";
import { DisconnectWarning, RemovalDescription } from "./DisconnectWarning";
import { Dropdown } from "./Dropdown";
import { GitSyncForm } from "./GitSyncForm";
import { CopyableValue } from "./Settings/CopyableValue";
import type { PrincipalPreview } from "./PrincipalPreviewList";
import { usePrincipalModals } from "../hooks/usePrincipalModals";
import { Icon } from "./Icon";
import { Input } from "./Input";
import {
  ScopeAccessTable,
  type ScopeAccessPendingHandle,
} from "./ScopeAccessTable";
import { PillBadge } from "./Settings/shared";
import {
  SettingsCard as Card,
  SettingsHeading as SectionHeading,
} from "./SettingsCard";
import { SkillsList } from "./SkillsList";
import { Text } from "./Text";
import { Toggle } from "./Toggle";
import { apiUrl } from "../lib/tenantOrigin";
import { navigate, routes } from "../lib/urls";

export type SpaceSettingsTab = "settings" | "access";

const TabPanel = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const NameField = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SlugInputBox = styled("label")`
  display: flex;
  align-items: stretch;
  background-color: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 8px;
  height: 32px;
  cursor: text;
  overflow: hidden;
  transition: box-shadow 100ms ease-out;

  &:focus-within {
    outline: 0.5px solid var(--control-bg);
    box-shadow: 0 0 0 3px var(--border-alpha);
  }
`;

const SlugInputField = styled("input")`
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  background: transparent;
  border: 0;
  outline: 0;
  color: var(--text-primary);
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;

  &::placeholder {
    opacity: 0.3;
  }

  &:focus::placeholder {
    opacity: 0.6;
  }
`;

const SlugSuffix = styled("span")`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0 12px;
  background-color: var(--surface-secondary);
  border-left: 0.5px solid var(--border-alpha);
  color: var(--text-secondary);
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  white-space: nowrap;
`;

// The rest of the space's host after the slug.
function useSpaceHostSuffix(): string | null {
  const customDomain = useTenantCustomDomain();
  const domain = publicDomain();
  return spacePublicHostnameSuffix(
    {
      publicWildcardDomain: domain.wildcardDomain,
      combinedSpaceTenantLabel: domain.combinedSpaceTenantLabel,
    },
    customDomain
  );
}

function SlugInput({
  hostSuffix,
  ...inputProps
}: {
  hostSuffix: string | null;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <SlugInputBox>
      <SlugInputField {...inputProps} maxLength={MAX_SLUG_LENGTH} />
      {hostSuffix !== null && <SlugSuffix>{hostSuffix}</SlugSuffix>}
    </SlugInputBox>
  );
}

const DiscoverabilityHeader = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 8px;
`;

const DiscoverabilityHeaderText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const JoinerListBox = styled("div")`
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;
`;

const JoinerRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  height: 64px;
`;

const JoinerInfo = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

function discoverabilityRoleOptions(
  customRoles: readonly {
    id: string;
    name: string;
    scopeKind: string;
    rolePermissions: ReadonlyArray<{ permission: string }>;
  }[]
): RoleOption[] {
  return rolesForScope("space", [...customRoles]).filter((opt) => {
    if (opt.builtinRoleId) {
      return !grantHasPermission(
        {
          builtinRoleId: opt.builtinRoleId,
          customRole: null,
        },
        "space:manage"
      );
    }
    if (opt.customRoleId) {
      const role = customRoles.find((r) => r.id === opt.customRoleId);
      if (!role) return false;
      return !role.rolePermissions.some(
        (p) => p.permission === "space:manage" || p.permission === "allow_all"
      );
    }
    return false;
  });
}

function roleOptionLabel(role: RoleOption): string {
  return role.label;
}

function currentRoleOption(
  options: RoleOption[],
  builtinRoleId: string | null | undefined,
  customRoleId: string | null | undefined
): RoleOption | undefined {
  if (builtinRoleId) {
    return options.find((o) => o.builtinRoleId === builtinRoleId);
  }
  if (customRoleId) {
    return options.find((o) => o.customRoleId === customRoleId);
  }
  return undefined;
}

type StagedGrantsRef = { current: ScopeAccessPendingHandle | null };

type EditProps = {
  mode: "edit";
  spaceId: string;
  canManageSpace: boolean;
  activeTab: SpaceSettingsTab;
  stagedGrantsRef?: StagedGrantsRef;
  onDeleted?: () => void;
  accessMapPanel?: ReactNode;
};

type CreateProps = {
  mode: "create";
  name: string;
  setName: (name: string) => void;
  slug: string;
  setSlug: (slug: string) => void;
  slugError: string | null;
  slugTaken: boolean;
  grants: GrantEntry[];
  setGrants: (grants: GrantEntry[]) => void;
  discoverability: DiscoverabilityValue;
  setDiscoverability: (value: DiscoverabilityValue) => void;
  activeTab: SpaceSettingsTab;
  stagedGrantsRef?: StagedGrantsRef;
};

type Props = EditProps | CreateProps;

function SpaceSettingsFormInner({
  spaceId,
  canManageSpace: canManageSpaceProp,
  activeTab,
  stagedGrantsRef,
  onDeleted,
  accessMapPanel,
}: {
  spaceId: string;
  canManageSpace: boolean;
  activeTab: SpaceSettingsTab;
  stagedGrantsRef?: StagedGrantsRef;
  onDeleted?: () => void;
  accessMapPanel?: ReactNode;
}) {
  const z = useZero();
  const displaySpaceName = useSpaceDisplayName();
  const { onOpenUser, onOpenGroup, onOpenServiceAccount } =
    usePrincipalModals();
  const [currentUser] = useQuery(queries.currentUser());
  const [space] = useQuery(queries.loadSpace({ spaceId }));
  const [spaceGrants] = useQuery(
    queries.roleGrantsByScope({ scopeId: spaceId })
  );
  const isPersonalSpace = space?.mode === "personal";
  const canManageSpace = canManageSpaceProp && !isPersonalSpace;
  const [allSpaces] = useSpaces({ settingsView: false });
  const [allApps] = useQuery(queries.connectorsWithUsages());
  const [allUserGroups] = useQuery(queries.selectableUserGroups());
  const [allUsers] = useQuery(queries.allUsers());
  const [allServiceAccounts] = useQuery(queries.serviceAccounts());
  const [customRoles] = useQuery(queries.allRolesLite());
  const allGroups = allUserGroups;
  const nameInputRef = useRef<HTMLInputElement>(null);

  const nameField = useNameField({
    currentName: space?.name,
    allItems: allSpaces,
    currentId: spaceId,
    onCommit: useCallback(
      (name: string) => {
        z.mutate(sharedMutators.updateSpace({ spaceId, name }));
      },
      [z, spaceId]
    ),
  });

  const hostSuffix = useSpaceHostSuffix();
  // A slug change breaks every existing link and email address under the old
  // host, so it is staged here and only mutated once the user confirms.
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);
  const slugField = useSlugField({
    currentSlug: space?.slug,
    allItems: allSpaces,
    currentId: spaceId,
    onCommit: useCallback((slug: string) => setPendingSlug(slug), []),
  });
  const confirmSlugChange = useCallback(() => {
    if (pendingSlug !== null) {
      z.mutate(sharedMutators.updateSpace({ spaceId, slug: pendingSlug }));
    }
    setPendingSlug(null);
  }, [z, spaceId, pendingSlug]);

  const {
    allGrants,
    assignedGroups,
    assignedUsers,
    roleOptions: allRoleOptions,
    commitGrants,
    removeGroup,
    removeUser,
    managerConfirmModal,
  } = useScopeGrants({
    scopeId: spaceId,
    scopeKind: "space",
    rawGrants: spaceGrants,
    allGroups,
    allUsers,
    customRoles,
    viewerId: currentUser?.id,
    resourceLabel: "space",
  });

  const { guard: guardSpaceAccess, modal: grantAccessModal } =
    useGrantSpaceAccess();

  const { userGroupIds: userGroupMemberships } = useGroupMemberships();

  const usableApps = useMemo(
    () =>
      usableSuppliersForSpace(
        "space_uses_connected_apps",
        spaceGrants,
        allApps,
        userGroupMemberships
      ),
    [allApps, spaceGrants, userGroupMemberships]
  );
  const { usedConnectorsForSpace } = useSpaceUsedSuppliers(allApps, [spaceId]);
  const usedApps = useMemo(
    () => usedConnectorsForSpace(spaceId),
    [usedConnectorsForSpace, spaceId]
  );

  const myGrants = useCurrentUserGrants();
  const manageableConnectorIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants,
        allApps.map((a) => a.id),
        SCOPE_MANAGE_GRANTS_PERMISSION.connected_app
      ),
    [myGrants, allApps]
  );
  const removableAppIds = useMemo(
    () =>
      new Set(
        usableApps
          .filter(
            (app) =>
              manageableConnectorIds.has(app.id) &&
              grantsWithoutSpacePrincipals(app.roleGrantsAsScope, spaceGrants)
                .removed.length > 0
          )
          .map((a) => a.id)
      ),
    [manageableConnectorIds, usableApps, spaceGrants]
  );

  const [allSkills] = useQuery(queries.skillsIndex());
  const usableSkills = useMemo(
    () =>
      usableSuppliersForSpace(
        "space_uses_skills",
        spaceGrants,
        allSkills,
        userGroupMemberships
      ),
    [allSkills, spaceGrants, userGroupMemberships]
  );
  const usedSkills = useMemo(
    () => usedSkillsForSpace(spaceGrants, allSkills),
    [spaceGrants, allSkills]
  );
  const manageableSkillIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants,
        allSkills.map((s) => s.id),
        SCOPE_MANAGE_GRANTS_PERMISSION.skill
      ),
    [myGrants, allSkills]
  );

  const removableSkillIds = useMemo(
    () =>
      new Set(
        usableSkills
          .filter(
            (skill) =>
              manageableSkillIds.has(skill.id) &&
              grantsWithoutSpacePrincipals(skill.roleGrantsAsScope, spaceGrants)
                .removed.length > 0
          )
          .map((s) => s.id)
      ),
    [manageableSkillIds, usableSkills, spaceGrants]
  );

  const [removingScope, setRemovingScope] = useState<{
    scopeKind: "connected_app" | "skill";
    id: string;
  } | null>(null);
  const removingSupplier = removingScope
    ? removingScope.scopeKind === "connected_app"
      ? (usableApps.find((a) => a.id === removingScope.id) ?? null)
      : (usableSkills.find((s) => s.id === removingScope.id) ?? null)
    : null;
  const removingName =
    removingSupplier?.name ||
    (removingScope?.scopeKind === "skill" ? "this skill" : "this connector");
  const computeRemovalImpact = useConnectorDisconnectImpact(
    removingScope?.scopeKind === "connected_app" ? removingScope.id : null
  );
  const removalNext = useMemo(() => {
    if (!removingSupplier) return [];
    const entries: GrantEntry[] = removingSupplier.roleGrantsAsScope
      .filter((g) => g.userGroupId || g.userId || g.serviceAccountId)
      .map((g) => ({
        userGroupId: g.userGroupId ?? undefined,
        userId: g.userId ?? undefined,
        serviceAccountId: g.serviceAccountId ?? undefined,
        builtinRoleId: g.builtinRoleId ?? undefined,
        customRoleId: g.customRoleId ?? undefined,
      }));
    return grantsWithoutSpacePrincipals(entries, spaceGrants).next;
  }, [removingSupplier, spaceGrants]);
  const removalImpact =
    removingScope?.scopeKind === "connected_app" && removingSupplier
      ? computeRemovalImpact(removingSupplier.roleGrantsAsScope, removalNext)
      : null;
  const chatCount = useAffectedChatCount(
    removingScope?.scopeKind === "connected_app" ? removingScope.id : null,
    removalImpact?.affectedUserIds ?? []
  );
  const removedPrincipals: PrincipalPreview[] = useMemo(() => {
    if (!removingSupplier) return [];
    const { removed } = grantsWithoutSpacePrincipals(
      removingSupplier.roleGrantsAsScope,
      spaceGrants
    );
    return removedPrincipalPreviews(removed, {
      groupById: new Map(allGroups.map((g) => [g.id, g] as const)),
      userById: new Map(
        allUsers.flatMap((u) => (u ? [[u.id, u] as const] : []))
      ),
      serviceAccountById: new Map(
        allServiceAccounts.map((sa) => [sa.id, sa] as const)
      ),
    });
  }, [removingSupplier, spaceGrants, allGroups, allUsers, allServiceAccounts]);

  const confirmRemoveSupplier = useCallback(() => {
    if (!removingScope || !removingSupplier) return;
    const { add, remove } = deriveScopeGrantDelta(
      removingSupplier.roleGrantsAsScope,
      removalNext
    );
    z.mutate(
      sharedMutators.patchScopeGrants({
        scopeId: removingScope.id,
        scopeKind: removingScope.scopeKind,
        add,
        remove,
      })
    );
    setRemovingScope(null);
  }, [z, removingScope, removingSupplier, removalNext]);

  const preflightCommitGrants = useCallback(
    (desired: GrantEntry[], onCommitted?: () => void) => {
      const gaps = computeSpaceMemberSupplierGap({
        currentSpaceGrants: spaceGrants,
        desiredSpaceGrants: grantEntriesToRows(desired, customRoles),
        connectors: usedApps,
        skills: usedSkills,
        memberships: userGroupMemberships,
        manageableConnectorIds,
        manageableSkillIds,
      });
      if (gaps.length === 0) {
        commitGrants(desired);
        return undefined;
      }
      // Deferred: keep the member staged behind the grant modal, and add them
      // (then run the caller's continuation, e.g. close) only once it confirms.
      guardSpaceAccess({
        spaceName: space?.name ?? "this space",
        gaps,
        proceed: () => {
          commitGrants(desired);
          onCommitted?.();
        },
      });
      return false;
    },
    [
      spaceGrants,
      customRoles,
      usedApps,
      usedSkills,
      userGroupMemberships,
      manageableConnectorIds,
      manageableSkillIds,
      guardSpaceAccess,
      commitGrants,
      space,
    ]
  );

  if (!space) {
    return null;
  }

  if (isPersonalSpace) return null;

  return (
    <>
      {managerConfirmModal}
      {grantAccessModal}

      <TabPanel style={{ display: activeTab === "settings" ? "flex" : "none" }}>
        <Card style={{ gap: 24 }}>
          <NameField>
            <Box horizontal={8}>
              <Text variant="text-small-md">Space name</Text>
            </Box>
            <Input
              ref={nameInputRef}
              value={nameField.value}
              onChange={(e) => nameField.setValue(e.target.value)}
              onBlur={nameField.commit}
              onKeyDown={(e) => {
                if (e.key === "Enter") nameInputRef.current?.blur();
              }}
              disabled={!canManageSpace}
              maxLength={MAX_SPACE_NAME_LENGTH}
              style={{ width: "100%" }}
            />
            {nameField.nameTaken && (
              <Box horizontal={8}>
                <Text variant="text-small" color="var(--text-danger)">
                  A space with this name already exists.
                </Text>
              </Box>
            )}
          </NameField>
          <NameField>
            <Box horizontal={8}>
              <Text variant="text-small-md">Space URL</Text>
            </Box>
            <SlugInput
              hostSuffix={hostSuffix}
              value={slugField.value}
              onChange={(e) => slugField.setValue(e.target.value)}
              onBlur={slugField.commit}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              disabled={!canManageSpace}
            />
            <Box horizontal={8}>
              {slugField.error ? (
                <Text variant="text-small" color="var(--text-danger)">
                  {slugField.error}
                </Text>
              ) : slugField.slugTaken ? (
                <Text variant="text-small" color="var(--text-danger)">
                  This space URL is already taken.
                </Text>
              ) : (
                <Text variant="text-small" color="var(--text-secondary)">
                  Your workflows’ links live at this address, so changing it
                  breaks any that are already in use.
                </Text>
              )}
            </Box>
          </NameField>
        </Card>

        <DiscoverabilitySection
          value={{
            defaultJoinBuiltinRoleId: space.defaultJoinBuiltinRoleId ?? null,
            defaultJoinCustomRoleId: space.defaultJoinCustomRoleId ?? null,
          }}
          onChange={(next) =>
            z.mutate(
              sharedMutators.updateSpace({
                spaceId,
                defaultJoinBuiltinRoleId: next.defaultJoinBuiltinRoleId,
                defaultJoinCustomRoleId: next.defaultJoinCustomRoleId,
              })
            )
          }
          customRoles={customRoles}
          canManage={canManageSpace}
        />

        {space?.slug && (
          <Card>
            <SectionHeading>
              <Text variant="text-regular-md">Git URL</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                Clone this space as a git repository. Authenticate with an API
                key as the password.
              </Text>
            </SectionHeading>
            <CopyableValue value={`${apiUrl()}/spaces/${space.slug}.git`}>
              <Text
                ellipsis
                variant="text-regular"
                color="var(--text-secondary)"
                style={{ flex: 1 }}
              >
                {`${apiUrl()}/spaces/${space.slug}.git`}
              </Text>
            </CopyableValue>
          </Card>
        )}

        <Card>
          <DiscoverabilityHeader>
            <DiscoverabilityHeaderText>
              <Text variant="text-regular-md">Peer-reviewed push live</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                Members can’t push a branch live if they worked on it — a peer
                with push access has to do it.
              </Text>
            </DiscoverabilityHeaderText>
            <Toggle
              isOn={space.blockSelfApproval ?? false}
              onToggle={() =>
                z.mutate(
                  sharedMutators.updateSpace({
                    spaceId,
                    blockSelfApproval: !space.blockSelfApproval,
                  })
                )
              }
              disabled={!canManageSpace}
            />
          </DiscoverabilityHeader>
        </Card>

        <GitSyncForm spaceId={spaceId} canManageSpace={canManageSpace} />

        {canManageSpace && (
          <DangerSection
            title="Delete space"
            description="Permanently deletes this space and everything in it — workflows, steps, and runs."
            actionLabel="Delete space"
            renderConfirm={({ open, onClose }) =>
              open ? (
                <DeleteSpaceModal
                  spaceId={spaceId}
                  spaceName={displaySpaceName(space)}
                  onClose={onClose}
                  onDeleted={onDeleted}
                />
              ) : null
            }
          />
        )}
      </TabPanel>

      <TabPanel style={{ display: activeTab === "access" ? "flex" : "none" }}>
        {accessMapPanel}
        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Access</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Add members and groups, and choose their role.
            </Text>
          </SectionHeading>
          <ScopeAccessTable
            allGrants={allGrants}
            assignedGroups={assignedGroups}
            assignedUsers={assignedUsers}
            roleOptions={allRoleOptions}
            allGroups={allGroups}
            allUsers={allUsers}
            customRoles={customRoles}
            canManage={canManageSpace}
            compact
            commitGrants={preflightCommitGrants}
            removeGroup={removeGroup}
            removeUser={removeUser}
            onOpenUser={onOpenUser}
            onOpenGroup={onOpenGroup}
            onOpenServiceAccount={onOpenServiceAccount}
            includeServiceAccounts
            stagedGrantsRef={stagedGrantsRef}
            defaultRole={defaultRoleForScope("space", allRoleOptions)}
          />
        </Card>

        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Connectors</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Connectors members can use in workflows here.
            </Text>
          </SectionHeading>
          <ConnectorsList
            apps={usableApps}
            onSelect={(app) => navigate(routes.modal.connector, { id: app.id })}
            emptyDescription="Connectors appear here when every member with space access can also use them."
            removableIds={removableAppIds}
            onRemove={(app) =>
              setRemovingScope({ scopeKind: "connected_app", id: app.id })
            }
          />
        </Card>

        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Skills</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Skills members can use here.
            </Text>
          </SectionHeading>
          <SkillsList
            skills={usableSkills}
            onSelect={(skill) => navigate(routes.modal.skill, { id: skill.id })}
            emptyDescription="Skills appear here when every member with space access can also use them."
            removableIds={removableSkillIds}
            onRemove={(skill) =>
              setRemovingScope({ scopeKind: "skill", id: skill.id })
            }
          />
        </Card>
      </TabPanel>

      <ConfirmModal
        open={pendingSlug !== null}
        onClose={() => setPendingSlug(null)}
        onConfirm={confirmSlugChange}
        title="Change the space URL?"
        description={
          hostSuffix
            ? `The space will move to ${pendingSlug ?? ""}${hostSuffix}. Existing links and email addresses at ${space.slug}${hostSuffix} will stop working.`
            : "Existing links to this space and its email addresses will stop working."
        }
        confirmLabel="Change URL"
        destructive
      />

      <ConfirmModal
        open={removingSupplier !== null}
        onClose={() => setRemovingScope(null)}
        onConfirm={confirmRemoveSupplier}
        title={`Remove ${removingName}?`}
        description={
          <RemovalDescription
            target={removingName}
            principals={removedPrincipals}
            workflows={removalImpact?.workflows.length ?? 0}
          />
        }
        confirmLabel="Remove"
        destructive
      >
        <DisconnectWarning
          principals={removedPrincipals}
          workflows={removalImpact?.workflows ?? []}
          gitSpaceNames={removalImpact?.gitSpaceNames ?? []}
          chatCount={chatCount}
        />
      </ConfirmModal>
    </>
  );
}

function CreateSpaceSettingsForm({
  name,
  setName,
  slug,
  setSlug,
  slugError,
  slugTaken,
  grants,
  setGrants,
  discoverability,
  setDiscoverability,
  activeTab,
  stagedGrantsRef,
}: Omit<CreateProps, "mode">) {
  const { onOpenUser, onOpenGroup, onOpenServiceAccount } =
    usePrincipalModals();
  const nameRef = useRef<HTMLInputElement>(null);

  // The dialog grabs focus on open (Modal calls el.focus()), so re-focus the
  // name field on the next frame so the user can start typing immediately.
  useEffect(() => {
    const id = requestAnimationFrame(() => nameRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, []);

  const [allSpaces] = useSpaces({ settingsView: false });
  const [allApps] = useQuery(queries.connectorsIndex());
  const [allUserGroups] = useQuery(queries.selectableUserGroups());
  const [allUsers] = useQuery(queries.allUsers());
  const [customRoles] = useQuery(queries.allRolesLite());
  const allGroups = allUserGroups;
  const hostSuffix = useSpaceHostSuffix();
  const normalized = normalizeName(name);
  const nameTaken =
    normalized.length > 0 &&
    (allSpaces ?? []).some((s) => namesMatch(s.name, normalized));

  const {
    allGrants,
    assignedGroups,
    assignedUsers,
    roleOptions: allRoleOptions,
    commitGrants,
    removeGroup,
    removeUser,
  } = useScopeGrants({
    scopeKind: "space",
    grants,
    onCommit: setGrants,
    allGroups,
    allUsers,
    customRoles,
  });

  const { userGroupIds: userGroupMemberships } = useGroupMemberships();

  const spaceGrantRows: GrantRow[] = useMemo(() => {
    const roleMap = new Map(customRoles.map((r) => [r.id, r]));
    return allGrants.map((g) => ({
      userGroupId: g.userGroupId ?? null,
      userId: g.userId ?? null,
      builtinRoleId: g.builtinRoleId ?? null,
      customRole: g.customRoleId ? (roleMap.get(g.customRoleId) ?? null) : null,
    }));
  }, [allGrants, customRoles]);

  const usableApps = useMemo(
    () =>
      usableSuppliersForSpace(
        "space_uses_connected_apps",
        spaceGrantRows,
        allApps,
        userGroupMemberships
      ),
    [allApps, spaceGrantRows, userGroupMemberships]
  );

  const [allSkills] = useQuery(queries.skillsIndex());
  const usableSkills = useMemo(
    () =>
      usableSuppliersForSpace(
        "space_uses_skills",
        spaceGrantRows,
        allSkills,
        userGroupMemberships
      ),
    [allSkills, spaceGrantRows, userGroupMemberships]
  );

  return (
    <>
      <TabPanel style={{ display: activeTab === "settings" ? "flex" : "none" }}>
        <Card style={{ gap: 24 }}>
          <NameField>
            <Box horizontal={8}>
              <Text variant="text-small-md">Space name</Text>
            </Box>
            <Input
              ref={nameRef}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Space name"
              maxLength={MAX_SPACE_NAME_LENGTH}
              style={{ width: "100%" }}
            />
            {nameTaken && (
              <Box horizontal={8}>
                <Text variant="text-small" color="var(--text-danger)">
                  A space with this name already exists.
                </Text>
              </Box>
            )}
          </NameField>
          <NameField>
            <Box horizontal={8}>
              <Text variant="text-small-md">Space URL</Text>
            </Box>
            <SlugInput
              hostSuffix={hostSuffix}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="space-url"
            />
            <Box horizontal={8}>
              {slugError ? (
                <Text variant="text-small" color="var(--text-danger)">
                  {slugError}
                </Text>
              ) : slugTaken ? (
                <Text variant="text-small" color="var(--text-danger)">
                  This space URL is already taken.
                </Text>
              ) : (
                <Text variant="text-small" color="var(--text-secondary)">
                  Your workflows’ links will live at this address.
                </Text>
              )}
            </Box>
          </NameField>
        </Card>

        <DiscoverabilitySection
          value={discoverability}
          onChange={setDiscoverability}
          customRoles={customRoles}
          canManage
        />
      </TabPanel>

      <TabPanel style={{ display: activeTab === "access" ? "flex" : "none" }}>
        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Access</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Add members and groups, and choose their role.
            </Text>
          </SectionHeading>
          <ScopeAccessTable
            allGrants={allGrants}
            assignedGroups={assignedGroups}
            assignedUsers={assignedUsers}
            roleOptions={allRoleOptions}
            allGroups={allGroups}
            allUsers={allUsers}
            customRoles={customRoles}
            canManage
            compact
            commitGrants={commitGrants}
            removeGroup={removeGroup}
            removeUser={removeUser}
            onOpenUser={onOpenUser}
            onOpenGroup={onOpenGroup}
            onOpenServiceAccount={onOpenServiceAccount}
            includeServiceAccounts
            stagedGrantsRef={stagedGrantsRef}
            defaultRole={defaultRoleForScope("space", allRoleOptions)}
          />
        </Card>

        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Connectors</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Connectors members can use in workflows here.
            </Text>
          </SectionHeading>
          <ConnectorsList
            apps={usableApps}
            onSelect={(app) => navigate(routes.modal.connector, { id: app.id })}
            emptyDescription="Connectors appear here when every member with space access can also use them."
          />
        </Card>

        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Skills</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Skills members can use here.
            </Text>
          </SectionHeading>
          <SkillsList
            skills={usableSkills}
            onSelect={(skill) => navigate(routes.modal.skill, { id: skill.id })}
            emptyDescription="Skills appear here when every member with space access can also use them."
          />
        </Card>
      </TabPanel>
    </>
  );
}

export type DiscoverabilityValue = {
  defaultJoinBuiltinRoleId: BuiltinRoleId | null;
  defaultJoinCustomRoleId: string | null;
};

function DiscoverabilitySection({
  value,
  onChange,
  customRoles,
  canManage,
}: {
  value: DiscoverabilityValue;
  onChange: (value: DiscoverabilityValue) => void;
  customRoles: readonly {
    id: string;
    name: string;
    scopeKind: string;
    rolePermissions: ReadonlyArray<{ permission: string }>;
  }[];
  canManage: boolean;
}) {
  const { defaultJoinBuiltinRoleId, defaultJoinCustomRoleId } = value;
  const discoverable = Boolean(
    defaultJoinBuiltinRoleId || defaultJoinCustomRoleId
  );
  const roleOptions = useMemo(
    () => discoverabilityRoleOptions(customRoles),
    [customRoles]
  );
  const selectedRole = currentRoleOption(
    roleOptions,
    defaultJoinBuiltinRoleId,
    defaultJoinCustomRoleId
  );

  const fallbackBuiltinId = "space_viewer";
  const fallbackOption = roleOptions.find(
    (o) => o.builtinRoleId === fallbackBuiltinId
  );

  const setRole = (role: RoleOption | null) => {
    onChange({
      defaultJoinBuiltinRoleId: role?.builtinRoleId ?? null,
      defaultJoinCustomRoleId: role?.customRoleId ?? null,
    });
  };

  const onToggle = () => {
    if (!canManage) return;
    if (discoverable) {
      setRole(null);
    } else if (fallbackOption) {
      setRole(fallbackOption);
    }
  };

  return (
    <Card>
      <DiscoverabilityHeader>
        <DiscoverabilityHeaderText>
          <Text variant="text-regular-md">Discoverability</Text>
          <Text variant="text-small" color="var(--text-secondary)">
            Lets anyone in your tenant find and join this space themselves, with
            the role below.
          </Text>
        </DiscoverabilityHeaderText>
        <Toggle isOn={discoverable} onToggle={onToggle} disabled={!canManage} />
      </DiscoverabilityHeader>
      {discoverable && (
        <JoinerListBox>
          <JoinerRow>
            <PlaceholderAvatar size={32} />
            <JoinerInfo>
              <Text variant="text-regular-md">Anyone in your tenant</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                Joins as
              </Text>
            </JoinerInfo>
            <Dropdown
              align="bottom-left"
              opener={
                <PillBadge icon="id-card-lanyard" interactive={canManage}>
                  {selectedRole ? roleOptionLabel(selectedRole) : "Pick a role"}
                </PillBadge>
              }
              sections={[
                {
                  items: roleOptions.map((role) => ({
                    type: "button" as const,
                    key: role.builtinRoleId ?? role.customRoleId ?? role.label,
                    left: <Icon name="id-card-lanyard" size={16} />,
                    label: role.label,
                    selected:
                      selectedRole?.builtinRoleId === role.builtinRoleId &&
                      selectedRole?.customRoleId === role.customRoleId,
                    onClick: () => {
                      if (!canManage) return;
                      setRole(role);
                    },
                  })),
                },
              ]}
            />
          </JoinerRow>
        </JoinerListBox>
      )}
    </Card>
  );
}

export function SpaceSettingsForm(props: Props) {
  if (props.mode === "create") {
    return (
      <CreateSpaceSettingsForm
        name={props.name}
        setName={props.setName}
        slug={props.slug}
        setSlug={props.setSlug}
        slugError={props.slugError}
        slugTaken={props.slugTaken}
        grants={props.grants}
        setGrants={props.setGrants}
        discoverability={props.discoverability}
        setDiscoverability={props.setDiscoverability}
        activeTab={props.activeTab}
        stagedGrantsRef={props.stagedGrantsRef}
      />
    );
  }
  return (
    <SpaceSettingsFormInner
      spaceId={props.spaceId}
      canManageSpace={props.canManageSpace}
      activeTab={props.activeTab}
      stagedGrantsRef={props.stagedGrantsRef}
      onDeleted={props.onDeleted}
      accessMapPanel={props.accessMapPanel}
    />
  );
}
